import React, { useContext, useEffect, useRef, useState } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { ConsentSettings, saveConsent } from './cookieService'
import { Button, Checkbox } from '../../components/UI'
import { PDFToolsTheme } from '../../lib/theme'
import { CookieBannerContext } from './CookieBannerContext'

export const CookieBanner = () => {
  const { isModalOpen, closeModal } = useContext(CookieBannerContext)

  return (
    <div>
      <Modal isOpen={isModalOpen} onClose={closeModal}></Modal>
    </div>
  )
}

// modal component

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  pointer-events: none;
  z-index: 111;

  @media (max-width: 768px), (max-height: 768px) {
    align-items: flex-end;
  }
`

const CookieBannerContainer = styled.div`
  display: flex;
  padding: 50px 60px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: var(--blue-blue-3, #0d59f2);
  pointer-events: auto;

  max-width: 1272px;

  @media (max-width: 768px), (max-height: 768px) {
    padding: 16px;
  }

  @media (max-height: 640px) and (orientation: portrait) {
    max-height: 100vh;
    overflow-y: scroll;
  }
`

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 80px;
  align-self: stretch;

  flex-direction: row;

  @media (max-width: 768px), (max-height: 768px) {
    flex-direction: column;

    gap: 32px;
  }
`

const CopyTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
`
const CopyTitle = styled.div`
  color: var(--white-white-1, var(--gray-white, #fff));
  /* Headlines/Desktop/H4 – 1.3 rem */
  font-family: Source Sans Pro;
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 31.2px */
`

const CopyDescription = styled.div`
  color: var(--white-white-1, var(--gray-white, #fff));
  font-feature-settings: 'dlig' on;
  font-family: Source Sans Pro;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  @media (max-width: 768px), (max-height: 768px) {
    justify-content: flex-end;
    width: 100%;
  }
`

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  initialFocusRef?: React.RefObject<HTMLElement> // Ref for the element to focus on modal open
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, initialFocusRef }) => {
  if (!isOpen) return null

  const modalRef = useRef<HTMLDivElement | null>(null)

  const [manageCookiesOpened, setManageCookiesOpened] = useState(false)

  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Set focus to the initialFocusRef or the modal itself
      const initialFocusElement = initialFocusRef?.current || modalRef.current
      initialFocusElement.focus()
    }
  }, [isOpen, initialFocusRef])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  return (
    <ModalOverlay>
      <ThemeProvider theme={PDFToolsTheme}>
        <CookieBannerContainer ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleKeyDown} tabIndex={-1}>
          {!manageCookiesOpened && (
            <ContentContainer>
              <CopyTextContainer>
                <CopyTitle>{'Our website uses cookies'}</CopyTitle>
                <CopyDescription>
                  We use cookies for analytical purposes and to show you advertising related to your preferences, based
                  on your browsing habits and profile. You can configure or block cookies by clicking on “Cookie
                  settings”. You can also accept all cookies by clicking on “Accept all cookies”. For more information,
                  please consult our Privacy Policy.
                </CopyDescription>
              </CopyTextContainer>
              <ButtonContainer>
                <Button
                  $size="small"
                  $variant="primary"
                  $inverted
                  style={{
                    color: '#0B50DA',
                  }}
                  onClick={() => {
                    saveConsent({ analytics: true, advertisement: true });
                    onClose()
                  }}
                >
                  Accept all cookies
                </Button>
                <Button
                  $size="small"
                  $variant="secondary"
                  style={{
                    color: 'white',
                  }}
                  onClick={() => {
                    setManageCookiesOpened(true)
                  }}
                >
                  Manage cookies
                </Button>
              </ButtonContainer>
            </ContentContainer>
          )}
          {manageCookiesOpened && <ManageCookies onClose={onClose} />}
        </CookieBannerContainer>
      </ThemeProvider>
    </ModalOverlay>
  )
}

const ManageCookies = ({ onClose }) => {
  const { consentSettings } = useContext(CookieBannerContext)

  const [formConsentSettings, setFormConsentSettings] = useState<ConsentSettings>({
    advertisement: true,
    analytics: true,
  })

  useEffect(() => {
    if (consentSettings) {
      setFormConsentSettings(consentSettings)
    }
  }, [])

  return (
    <ManageCookiesContent>
      <div>
        <CopyTitle>Manage cookies</CopyTitle>
        <ManageCookiesDescription>
          This site uses cookies. Because we respect your right to privacy, you can choose not to allow some types of
          cookies.
        </ManageCookiesDescription>
      </div>
      <ManageCookiesBottomPart>
        <div>
          <ConsentCategoryInput
            checked={true}
            title="Necessary"
            description="These cookies are necessary for the website to function and cannot be switched off in our systems."
            disabled
          />
          <ConsentCategoryInput
            checked={formConsentSettings?.advertisement}
            title="Advertisement (Optional)"
            description="These cookies make our ads more engaging and valuable to site visitors."
            onChange={e =>
              setFormConsentSettings(currState => ({
                ...currState,
                advertisement: !currState?.advertisement,
              }))
            }
          />
          <ConsentCategoryInput
            checked={formConsentSettings?.analytics}
            title="Analytics (Optional)"
            description="These cookies help us to understand how visitors engage with the website."
            onChange={e =>
              setFormConsentSettings(currState => ({
                ...currState,
                analytics: !currState?.analytics,
              }))
            }
          />
          {/* leaving it here commented out in case we need add it */}
          {/* <ConsentCategoryInput
            checked={formConsentSettings?.functional}
            title="Functionality (Optional) "
            description="We use a set of cookies that are optional for the website to function."
            onChange={e =>
              setFormConsentSettings(currState => ({
                ...currState,
                functional: !currState?.functional,
              }))
            }
          /> */}
        </div>

        <ButtonRow>
          <Button
            $size="small"
            $variant="primary"
            $inverted
            style={{
              color: '#0B50DA',
            }}
            onClick={() => {
              saveConsent({ analytics: true, advertisement: true })
              onClose()
            }}
          >
            Accept all cookies
          </Button>
          <Button
            $variant="primary"
            $size="small"
            $inverted
            style={{
              color: '#0B50DA',
            }}
            onClick={() => {
              saveConsent(formConsentSettings)
              onClose()
            }}
          >
            Save cookie settings
          </Button>
        </ButtonRow>
      </ManageCookiesBottomPart>
    </ManageCookiesContent>
  )
}

const ManageCookiesBottomPart = styled.div`
  width: 100%;
`

const ButtonRow = styled.div`
  margin-top: 34px;
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px), (max-height: 768px) {
    margin-top: 16px;
    gap: 10px;
  }

  @media (max-width: 576px) {
    align-items: stretch;
    flex-direction: column;

    padding-top: 12px;
    padding-bottom: 12px;
  }
`

const ConsentCategoryInput = ({
  title,
  description,
  onChange,
  checked,
  ...restCheckBox
}: {
  title: string
  description: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  checked: boolean
  [key: string]: any
}) => {
  return (
    <ConsentCategory>
      <ConsentCategoryTitleAndCheckBoxContainer disabled={restCheckBox?.disabled}>
        <ConsentCategoryTitle>{title}</ConsentCategoryTitle>
        <Checkbox checked={checked} onChange={onChange} variant="dark" disabled={false} size={18} {...restCheckBox} />
      </ConsentCategoryTitleAndCheckBoxContainer>
      <ConsentCategoryDescription>{description}</ConsentCategoryDescription>
    </ConsentCategory>
  )
}

const ConsentCategoryTitleAndCheckBoxContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`

const ManageCookiesContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;

  color: white;

  height: 100%;

  @media (max-width: 768px), (max-height: 768px) {
    gap: 16px;
  }

  @media (max-height: 768px) and (orientation: landscape) {
    overflow-y: scroll;
    max-height: 65vh;
  }
`
const ConsentCategory = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;

  width: 100%;
  padding-right: 10px;

  background: rgba(0, 0, 0, 0.1);

  border: 2px solid var(--white-white-280, rgba(255, 255, 255, 0.8));
  border-bottom: 0px;

  &:first-child {
    border-radius: 20px 20px 0px 0px;
  }

  &:last-child {
    border-radius: 0px 0px 20px 20px;
    border-bottom: 2px solid var(--white-white-280, rgba(255, 255, 255, 0.8));
  }

  @media (max-width: 768px), (max-height: 768px) {
    gap: 4px;
    padding: 16px;
  }
`

const ConsentCategoryTitle = styled.div`
  color: var(--white-white-1, var(--gray-white, #fff));
  font-feature-settings: 'salt' on;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 110%; /* 17.6px */
`

const ConsentCategoryDescription = styled.div`
  color: var(--white-white-1, var(--gray-white, #fff));
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 166.667% */
`

const ManageCookiesDescription = styled.div`
  color: var(--white-white-1, var(--gray-white, #fff));
  font-feature-settings: 'dlig' on;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`
